"use client";

import {
  PopoverClose,
  PopoverContent,
  PopoverRoot,
  PopoverSection,
  PopoverTrigger,
} from "@elion-health/ui";
import { Fragment } from "react";
import type { MapLinkComponent } from "./link";

export interface BreadcrumbSibling {
  id: string;
  label: string;
  href: string;
  description?: string | null;
}

export interface BreadcrumbMenuCrumb {
  label: string;
  href: string;
  /** Other nodes at this level, offered in a dropdown beside the crumb. */
  siblings?: {
    heading: string;
    items: BreadcrumbSibling[];
  };
}

export interface BreadcrumbMenuProps {
  /** Root first; the last crumb is the current page. */
  crumbs: BreadcrumbMenuCrumb[];
  linkComponent?: MapLinkComponent;
}

// The base `a` rule in tokens.css is unlayered, so it outranks utilities:
// links here take `!` to drop its underline and inherit the crumb's color.
/**
 * A breadcrumb whose crumbs can each open a menu of their siblings, so a
 * reader can hop sideways (to another function in the same area) as well as
 * up. The DS `Breadcrumb` renders plain anchors and cannot host menus, so this
 * composes the DS `Popover` instead.
 */
export function BreadcrumbMenu({
  crumbs,
  linkComponent: Link = "a",
}: BreadcrumbMenuProps) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-0.5 text-[12.5px]">
        {crumbs.map((crumb, index) => {
          const current = index === crumbs.length - 1;
          return (
            <Fragment key={crumb.href}>
              {index > 0 && (
                <li aria-hidden={true} className="px-0.5 text-(--fg-subtle)">
                  /
                </li>
              )}
              <li
                className={`inline-flex items-center ${
                  current
                    ? "font-semibold text-(--fg-primary)"
                    : "font-medium text-(--fg-secondary)"
                }`}
              >
                <Link
                  href={crumb.href}
                  aria-current={current ? "page" : undefined}
                  className="rounded-(--radius-field) px-1.5 py-0.5 no-underline! text-inherit! transition-colors hover:bg-(--bg-hover) hover:text-(--fg-primary)! focus-visible:outline-2 focus-visible:outline-(--fg-primary)"
                >
                  {crumb.label}
                </Link>
                {crumb.siblings && (
                  <SiblingMenu
                    label={crumb.label}
                    siblings={crumb.siblings}
                    current={crumb.href}
                    Link={Link}
                  />
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

function SiblingMenu({
  label,
  siblings,
  current,
  Link,
}: {
  label: string;
  siblings: NonNullable<BreadcrumbMenuCrumb["siblings"]>;
  current: string;
  Link: MapLinkComponent;
}) {
  return (
    <PopoverRoot>
      <PopoverTrigger
        aria-label={`Switch from ${label}`}
        className="group ml-0.5 inline-flex size-5 cursor-pointer items-center justify-center rounded-(--radius-field) text-(--fg-muted) transition-colors hover:bg-(--bg-hover) hover:text-(--fg-primary) focus-visible:outline-2 focus-visible:outline-(--fg-primary)"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden={true}
          className="transition-transform group-data-[state=open]:rotate-180"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[70vh] w-[320px] overflow-y-auto"
      >
        <PopoverSection title={siblings.heading}>
          <ul className="flex flex-col gap-0.5">
            {siblings.items.map((item) => {
              const isCurrent = item.href === current;
              return (
                <li key={item.id}>
                  <PopoverClose asChild>
                    <Link
                      href={item.href}
                      aria-current={isCurrent ? "page" : undefined}
                      className={`block rounded-(--radius-field) px-2 py-1.5 no-underline! text-inherit! transition-colors hover:bg-(--bg-hover) focus-visible:outline-2 focus-visible:outline-(--fg-primary) ${
                        isCurrent ? "bg-(--bg-canvas)" : ""
                      }`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span
                          className={`text-[12px] font-medium ${
                            isCurrent
                              ? "text-(--fg-primary)"
                              : "text-(--fg-secondary)"
                          }`}
                        >
                          {item.label}
                        </span>
                        {isCurrent && (
                          <span className="shrink-0 text-[10px] font-semibold text-(--fg-muted)">
                            Current
                          </span>
                        )}
                      </span>
                      {item.description && (
                        <span className="mt-0.5 line-clamp-2 block text-[11px] leading-snug text-(--fg-muted)">
                          {item.description}
                        </span>
                      )}
                    </Link>
                  </PopoverClose>
                </li>
              );
            })}
          </ul>
        </PopoverSection>
      </PopoverContent>
    </PopoverRoot>
  );
}
